// Admin CRUD: sourcing items with multi-origin support
// Product-level data (name, brand, images) in main dialog
// Origin-specific data (MOQ, cost, port) editable in main dialog when an origin is selected (or in Origins dialog)

import { useState, useEffect, Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSourcingItems, useBrands, useProductFamilies, useOrigins, useOriginPorts, useLoadingMethods, useSuppliers, useMoqUnits, useCostIncoterms, useProductStatuses, useSourcingItemOrigins, useSaveItemOrigin, useDeleteItemOrigin, useSetActiveOrigin, useCompanySettings } from '@/hooks/use-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { SourcingItem, SourcingItemOrigin } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { isLoadingMethodPalletized } from '@/lib/constants';
import { reportCaughtError } from '@/lib/report-error';
import { safeTrim, searchMatch } from '@/lib/utils';
import { postWorkerApi } from '@/lib/worker-api';
import { ImageUploadField } from '@/components/store/ImageUploadField';
import { SecondaryImagesList } from '@/components/store/SecondaryImagesList';
import { StoreThumbnailImg } from '@/components/store/StoreThumbnailImg';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Check, Globe, Plus, Trash2, Package, Clock, CreditCard, MapPin, Truck, Scale, Sparkles, Loader2, Search, Merge, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/** Returns human-readable labels for missing logistics data on the active origin (for tooltip). */
function getMissingLogisticsLabels(
  origin: SourcingItemOrigin | null,
  loadingMethods: { id: string; name: string }[]
): string[] {
  if (!origin) return ['Sin origen activo'];
  const out: string[] = [];
  if (!origin.origin_id) out.push('Origen (país)');
  if (origin.origin_port_id == null || origin.origin_port_id === '') out.push('Puerto origen');
  if (!origin.loading_method_id) out.push('Método de carga');
  if (origin.moq_unit_id == null || origin.moq_unit_id === '') out.push('MOQ unidad');
  if (origin.moq_value == null || Number(origin.moq_value) <= 0) out.push('MOQ valor');
  if (origin.gross_weight_per_box_kg == null || Number(origin.gross_weight_per_box_kg) <= 0) out.push('Peso/caja (kg)');
  if (origin.units_per_box == null || Number(origin.units_per_box) <= 0) out.push('Unidades por caja');
  if (origin.boxes_per_container == null || Number(origin.boxes_per_container) <= 0) out.push('Cajas por contenedor');
  if (!origin.recommended_container_type) out.push('Contenedor recomendado');
  const lmName = loadingMethods.find((m) => m.id === origin.loading_method_id)?.name;
  const isPalletized = isLoadingMethodPalletized(lmName);
  if (isPalletized && (origin.boxes_per_pallet == null || Number(origin.boxes_per_pallet) <= 0)) out.push('Cajas por pallet');
  if (!isPalletized) {
    if (origin.box_length_cm == null || Number(origin.box_length_cm) <= 0) out.push('Largo caja (cm)');
    if (origin.box_width_cm == null || Number(origin.box_width_cm) <= 0) out.push('Ancho caja (cm)');
    if (origin.box_height_cm == null || Number(origin.box_height_cm) <= 0) out.push('Alto caja (cm)');
  }
  return out;
}

export default function AdminSourcingItems() {
  const { data: items, isLoading } = useSourcingItems(null, { admin: true });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkField, setBulkField] = useState<string>('brand_id');
  const [bulkValue, setBulkValue] = useState('');
  const [openNormalizeLoadModal, setOpenNormalizeLoadModal] = useState(false);
  const [normalizeLoadInstruction, setNormalizeLoadInstruction] = useState('');
  const [normalizeLoadLoading, setNormalizeLoadLoading] = useState(false);
  const [filterFamilyId, setFilterFamilyId] = useState<string>('');
  const { data: brands } = useBrands();
  const { data: families } = useProductFamilies(null);
  const { data: statuses } = useProductStatuses();
  const { data: loadingMethods } = useLoadingMethods();
  const { data: companySettings } = useCompanySettings();
  const currentPseoVersion = companySettings?.pseo_config?.version ?? 1;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Product dialog state
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SourcingItem | null>(null);
  const [internal_name, setInternal_name] = useState('');
  const [slug, setSlug] = useState('');
  const [brand_id, setBrand_id] = useState('');
  const [sales_format, setSales_format] = useState('');
  const [packshot_image, setPackshot_image] = useState('');
  const [secondary_images, setSecondary_images] = useState<string[]>([]);
  const [barcode_ean, setBarcode_ean] = useState('');
  const [status_id, setStatus_id] = useState('');
  const [family_id, setFamily_id] = useState('');

  // Origins dialog state
  const [originsOpen, setOriginsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SourcingItem | null>(null);

  // Convert product to origin of another: source item (to merge) and dialog open
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertSourceItem, setConvertSourceItem] = useState<SourcingItem | null>(null);

  // Generate image options modal (product only): same modal from edit dialog and from table row
  const [generateImageModalOpen, setGenerateImageModalOpen] = useState(false);
  const [generateImageModalItem, setGenerateImageModalItem] = useState<SourcingItem | null>(null);
  const [genTarget, setGenTarget] = useState<'packshot' | 'secondary'>('packshot');
  const [genImageType, setGenImageType] = useState<'retail' | 'clean' | 'b2b'>('clean');
  const [genReferenceUrl, setGenReferenceUrl] = useState('');
  const [genIncludeBrand, setGenIncludeBrand] = useState(true);
  const [genPrompt, setGenPrompt] = useState('');
  const [genPromptLoaded, setGenPromptLoaded] = useState(false);
  const [genLoadingPrompt, setGenLoadingPrompt] = useState(false);
  const [genGenerating, setGenGenerating] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchSeoGenerating, setBatchSeoGenerating] = useState(false);
  const [batchSeoProgress, setBatchSeoProgress] = useState('');
  /** Item ids for which generate was triggered from modal (row shows spinner until request completes). */
  const [generatingOneIds, setGeneratingOneIds] = useState<Set<string>>(new Set());
  /** Bump when AI regenerates cover so preview img reloads (same URL, new file in storage). */
  const [packshotPreviewKey, setPackshotPreviewKey] = useState(0);

  // pSEO edit form fields (read from/write to seo_metadata JSONB)
  const [seoMetaTitle, setSeoMetaTitle] = useState('');
  const [seoMetaDescription, setSeoMetaDescription] = useState('');
  const [seoAssignedPorts, setSeoAssignedPorts] = useState('');
  const [seoSectionOpen, setSeoSectionOpen] = useState(false);
  const [seoRegenerating, setSeoRegenerating] = useState(false);

  // pSEO filter: 'all' | 'with' | 'without' | 'outdated' (has pSEO but not current version)
  const [filterPseo, setFilterPseo] = useState<'all' | 'with' | 'without' | 'outdated'>('all');
  /** When true, batch pSEO will also update slug to new AI slug (current slug goes to 301 history). Use once for v1→v2 upgrade. */
  const [upgradeSlugWhenGenerating, setUpgradeSlugWhenGenerating] = useState(false);

  // First origin form (when creating new item) — same fields as OriginsDialog
  const [firstOrigin_id, setFirstOrigin_id] = useState('');
  const [firstOrigin_port_id, setFirstOrigin_port_id] = useState('');
  const [firstSupplier_id, setFirstSupplier_id] = useState('');
  const [firstCost_usd, setFirstCost_usd] = useState('');
  const [firstCost_incoterm_id, setFirstCost_incoterm_id] = useState('');
  const [firstUnits_per_box, setFirstUnits_per_box] = useState('');
  const [firstBoxes_per_pallet, setFirstBoxes_per_pallet] = useState('');
  const [firstBoxes_per_container, setFirstBoxes_per_container] = useState('');
  const [firstGross_weight_per_box_kg, setFirstGross_weight_per_box_kg] = useState('');
  const [firstMoq_value, setFirstMoq_value] = useState('');
  const [firstMoq_unit_id, setFirstMoq_unit_id] = useState('');
  const [firstProduction_time_days, setFirstProduction_time_days] = useState('');
  const [firstPayment_terms, setFirstPayment_terms] = useState('');
  const [firstObservaciones, setFirstObservaciones] = useState('');
  const [firstInternal_notes, setFirstInternal_notes] = useState('');
  const [firstReferencia, setFirstReferencia] = useState('');
  const [firstLoading_method_id, setFirstLoading_method_id] = useState('');
  const [firstRecommended_container_type, setFirstRecommended_container_type] = useState('');
  const [firstBox_length_cm, setFirstBox_length_cm] = useState('');
  const [firstBox_width_cm, setFirstBox_width_cm] = useState('');
  const [firstBox_height_cm, setFirstBox_height_cm] = useState('');

  // When editing: selected origin (for viewing/editing its data in this dialog). Changes apply to this origin.
  const [selectedOriginId, setSelectedOriginId] = useState<string | null>(null);
  // Edit-origin form state (when editing an item and an origin is selected)
  const [editOrigin_port_id, setEditOrigin_port_id] = useState('');
  const [editOrigin_supplier_id, setEditOrigin_supplier_id] = useState('');
  const [editOrigin_cost_usd, setEditOrigin_cost_usd] = useState('');
  const [editOrigin_cost_incoterm_id, setEditOrigin_cost_incoterm_id] = useState('');
  const [editOrigin_units_per_box, setEditOrigin_units_per_box] = useState('');
  const [editOrigin_boxes_per_pallet, setEditOrigin_boxes_per_pallet] = useState('');
  const [editOrigin_boxes_per_container, setEditOrigin_boxes_per_container] = useState('');
  const [editOrigin_gross_weight_per_box_kg, setEditOrigin_gross_weight_per_box_kg] = useState('');
  const [editOrigin_moq_value, setEditOrigin_moq_value] = useState('');
  const [editOrigin_moq_unit_id, setEditOrigin_moq_unit_id] = useState('');
  const [editOrigin_production_time_days, setEditOrigin_production_time_days] = useState('');
  const [editOrigin_payment_terms, setEditOrigin_payment_terms] = useState('');
  const [editOrigin_observaciones, setEditOrigin_observaciones] = useState('');
  const [editOrigin_internal_notes, setEditOrigin_internal_notes] = useState('');
  const [editOrigin_referencia, setEditOrigin_referencia] = useState('');
  const [editOrigin_loading_method_id, setEditOrigin_loading_method_id] = useState('');
  const [editOrigin_recommended_container_type, setEditOrigin_recommended_container_type] = useState('');
  const [editOrigin_box_length_cm, setEditOrigin_box_length_cm] = useState('');
  const [editOrigin_box_width_cm, setEditOrigin_box_width_cm] = useState('');
  const [editOrigin_box_height_cm, setEditOrigin_box_height_cm] = useState('');

  const { data: allOrigins } = useOrigins();
  const { data: allOriginPorts } = useOriginPorts(); // All ports for dropdown labels when editing
  const { data: itemOrigins } = useSourcingItemOrigins(editing?.id ?? null, { enabled: !!editing?.id });
  const { data: editOriginPorts } = useOriginPorts(
    itemOrigins?.find((o) => o.id === selectedOriginId)?.origin_id ?? undefined
  );
  const { data: firstOriginPorts } = useOriginPorts(firstOrigin_id || undefined);
  const { data: suppliers } = useSuppliers();
  const { data: moqUnits } = useMoqUnits();
  const { data: costIncoterms } = useCostIncoterms();

  // When editing and origins load, select the active origin (or first) so its data is shown and editable
  useEffect(() => {
    if (!editing || !itemOrigins?.length) {
      setSelectedOriginId(null);
      return;
    }
    const active = itemOrigins.find((o) => o.is_active) ?? itemOrigins[0];
    setSelectedOriginId(active.id);
  }, [editing?.id, itemOrigins]);

  // When selected origin changes, fill edit-origin form from that origin
  useEffect(() => {
    if (!selectedOriginId || !itemOrigins) return;
    const o = itemOrigins.find((x) => x.id === selectedOriginId);
    if (!o) return;
    setEditOrigin_port_id(o.origin_port_id ?? '');
    setEditOrigin_supplier_id(o.supplier_id ?? '');
    setEditOrigin_cost_usd(String(o.cost_usd ?? ''));
    setEditOrigin_cost_incoterm_id(o.cost_incoterm_id ?? '');
    setEditOrigin_units_per_box(String(o.units_per_box ?? ''));
    setEditOrigin_boxes_per_pallet(String(o.boxes_per_pallet ?? ''));
    setEditOrigin_boxes_per_container(String(o.boxes_per_container ?? ''));
    setEditOrigin_gross_weight_per_box_kg(String(o.gross_weight_per_box_kg ?? ''));
    setEditOrigin_moq_value(String(o.moq_value ?? ''));
    setEditOrigin_moq_unit_id(o.moq_unit_id ?? '');
    setEditOrigin_production_time_days(String(o.production_time_days ?? ''));
    setEditOrigin_payment_terms(o.payment_terms ?? '');
    setEditOrigin_observaciones(o.observaciones ?? '');
    setEditOrigin_internal_notes(o.internal_notes ?? '');
    setEditOrigin_referencia(o.referencia ?? '');
    setEditOrigin_loading_method_id(o.loading_method_id ?? '');
    setEditOrigin_recommended_container_type(o.recommended_container_type ?? '');
    setEditOrigin_box_length_cm(o.box_length_cm != null ? String(o.box_length_cm) : '');
    setEditOrigin_box_width_cm(o.box_width_cm != null ? String(o.box_width_cm) : '');
    setEditOrigin_box_height_cm(o.box_height_cm != null ? String(o.box_height_cm) : '');
  }, [selectedOriginId, itemOrigins]);

  // Save product; when creating, also insert first origin. When editing with selected origin, also update that origin.
  type SaveArg = { originId?: string | null; originPayload?: Record<string, unknown> };
  const saveMutation = useMutation({
    mutationFn: async (arg?: SaveArg) => {
      // Build seo_metadata JSONB, preserving any extra fields the AI may have added
      const existingSeo = editing?.seo_metadata ?? {};
      const portsArray = seoAssignedPorts.trim()
        ? seoAssignedPorts.split(',').map((p) => p.trim()).filter(Boolean)
        : [];
      const seo_metadata = {
        ...existingSeo,
        meta_title: seoMetaTitle.trim() || undefined,
        meta_description: seoMetaDescription.trim() || undefined,
        assigned_ports: portsArray.length ? portsArray : undefined,
        search_intent: 'b2b_wholesale_latam',
      };
      const row = {
        internal_name,
        slug,
        brand_id: brand_id || undefined,
        sales_format,
        packshot_image: packshot_image || null,
        secondary_images: secondary_images.filter(Boolean).length ? secondary_images.filter(Boolean) : null,
        barcode_ean: barcode_ean || null,
        status_id: status_id || undefined,
        seo_metadata,
      };
      if (editing) {
        const { error } = await supabase.from('sourcing_items').update(row).eq('id', editing.id);
        if (error) throw error;
        await supabase.from('sourcing_item_families').delete().eq('item_id', editing.id);
        if (family_id) {
          const { error: jErr } = await supabase.from('sourcing_item_families').insert({ item_id: editing.id, family_id });
          if (jErr) throw jErr;
        }
        // Apply origin data to the selected origin (changes in this dialog apply to that origin)
        if (arg?.originId && arg?.originPayload) {
          const { error: originErr } = await supabase
            .from('sourcing_item_origins')
            .update(arg.originPayload)
            .eq('id', arg.originId);
          if (originErr) throw originErr;
        }
        // Sync thumbnail from current packshot (manual upload or "Usar como portada"); removes old thumb from Storage.
        if (packshot_image?.trim()) {
          try {
            await postWorkerApi<{ ok?: boolean }>('/api/sync-item-thumbnail', { itemId: editing.id });
          } catch {
            // Non-blocking; list/og will use main image or existing thumbnail
          }
        }
      } else {
        if (!firstOrigin_id?.trim()) throw new Error('Debe indicar al menos un origen (país).');
        const { data: inserted, error: insertError } = await supabase
          .from('sourcing_items')
          .insert(row)
          .select('id')
          .single();
        if (insertError) throw insertError;
        const newItemId = inserted.id;
        const { error: originError } = await supabase.from('sourcing_item_origins').insert({
          sourcing_item_id: newItemId,
          origin_id: firstOrigin_id,
          origin_port_id: firstOrigin_port_id || null,
          supplier_id: firstSupplier_id || null,
          cost_usd: firstCost_usd ? Number(firstCost_usd) : null,
          cost_incoterm_id: firstCost_incoterm_id || null,
          loading_method_id: firstLoading_method_id || null,
          recommended_container_type: firstRecommended_container_type || null,
          units_per_box: Number(firstUnits_per_box) || 0,
          boxes_per_pallet: firstBoxes_per_pallet ? Number(firstBoxes_per_pallet) : null,
          boxes_per_container: Number(firstBoxes_per_container) || 0,
          gross_weight_per_box_kg: Number(firstGross_weight_per_box_kg) || 0,
          moq_value: Number(firstMoq_value) || 0,
          moq_unit_id: firstMoq_unit_id || null,
          production_time_days: firstProduction_time_days ? Number(firstProduction_time_days) : null,
          payment_terms: firstPayment_terms || null,
          observaciones: firstObservaciones || null,
          internal_notes: firstInternal_notes?.trim() || null,
          referencia: firstReferencia?.trim() || null,
          is_active: true,
          box_length_cm: firstBox_length_cm === '' ? null : Number(firstBox_length_cm),
          box_width_cm: firstBox_width_cm === '' ? null : Number(firstBox_width_cm),
          box_height_cm: firstBox_height_cm === '' ? null : Number(firstBox_height_cm),
        });
        if (originError) throw originError;
        if (family_id) {
          const { error: jErr } = await supabase.from('sourcing_item_families').insert({ item_id: newItemId, family_id });
          if (jErr) throw jErr;
        }
        // Sync thumbnail from packshot so list/WhatsApp have a small image; removes old thumb from Storage.
        if (packshot_image?.trim()) {
          try {
            await postWorkerApi<{ ok?: boolean }>('/api/sync-item-thumbnail', { itemId: newItemId });
          } catch {
            // Non-blocking
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins'] });
      if (editing?.id) queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins', editing.id] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_families'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'item_family_category'] });
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: editing ? 'Item actualizado' : 'Item creado' });
    },
    onError: (e) => {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'save_item', editing_id: editing?.id });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Remove product images from Storage before deleting the row (avoid orphans).
      await postWorkerApi<{ ok?: boolean; deleted?: number }>('/api/delete-product-storage-images', { itemId: id });
      const { error } = await supabase.from('sourcing_items').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_families'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'item_family_category'] });
      toast({ title: 'Item deleted' });
    },
    onError: (e) => {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'delete_item' });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' });
    },
  });

  const bulkApplyMutation = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      const originFields = new Set([
        'origin_id', 'origin_port_id', 'supplier_id', 'cost_usd', 'cost_incoterm_id', 'loading_method_id',
        'recommended_container_type',
        'units_per_box', 'boxes_per_pallet', 'boxes_per_container', 'gross_weight_per_box_kg',
        'moq_value', 'moq_unit_id', 'production_time_days', 'payment_terms', 'observaciones', 'internal_notes', 'referencia',
        'box_length_cm', 'box_width_cm', 'box_height_cm',
      ]);
      if (bulkField === 'family_id') {
        for (const id of ids) {
          await supabase.from('sourcing_item_families').delete().eq('item_id', id);
          if (bulkValue?.trim()) {
            const { error } = await supabase.from('sourcing_item_families').insert({ item_id: id, family_id: bulkValue.trim() });
            if (error) throw error;
          }
        }
      } else if (originFields.has(bulkField)) {
        const { data: activeRows } = await supabase
          .from('sourcing_item_origins')
          .select('id')
          .in('sourcing_item_id', ids)
          .eq('is_active', true);
        const originIds = (activeRows ?? []).map((r) => r.id);
        if (originIds.length === 0) throw new Error('Ninguno de los ítems seleccionados tiene un origen activo. Activa un origen en cada uno desde «Origins».');
        const payload: Record<string, unknown> = {};
        if (bulkField === 'origin_id') payload.origin_id = bulkValue || null;
        else if (bulkField === 'origin_port_id') payload.origin_port_id = bulkValue || null;
        else if (bulkField === 'supplier_id') payload.supplier_id = bulkValue || null;
        else if (bulkField === 'cost_usd') payload.cost_usd = bulkValue === '' ? null : Number(bulkValue);
        else if (bulkField === 'cost_incoterm_id') payload.cost_incoterm_id = bulkValue || null;
        else if (bulkField === 'loading_method_id') payload.loading_method_id = bulkValue || null;
        else if (bulkField === 'recommended_container_type') payload.recommended_container_type = bulkValue || null;
        else if (bulkField === 'units_per_box') payload.units_per_box = Number(bulkValue) || 0;
        else if (bulkField === 'boxes_per_pallet') payload.boxes_per_pallet = bulkValue === '' ? null : Number(bulkValue);
        else if (bulkField === 'boxes_per_container') payload.boxes_per_container = Number(bulkValue) || 0;
        else if (bulkField === 'gross_weight_per_box_kg') payload.gross_weight_per_box_kg = Number(bulkValue) || 0;
        else if (bulkField === 'moq_value') payload.moq_value = Number(bulkValue) || 0;
        else if (bulkField === 'moq_unit_id') payload.moq_unit_id = bulkValue || null;
        else if (bulkField === 'production_time_days') payload.production_time_days = bulkValue === '' ? null : Number(bulkValue);
        else if (bulkField === 'payment_terms') payload.payment_terms = bulkValue || null;
        else if (bulkField === 'observaciones') payload.observaciones = bulkValue || null;
        else if (bulkField === 'internal_notes') payload.internal_notes = bulkValue?.trim() || null;
        else if (bulkField === 'referencia') payload.referencia = bulkValue?.trim() || null;
        else if (bulkField === 'box_length_cm') payload.box_length_cm = bulkValue === '' ? null : Number(bulkValue);
        else if (bulkField === 'box_width_cm') payload.box_width_cm = bulkValue === '' ? null : Number(bulkValue);
        else if (bulkField === 'box_height_cm') payload.box_height_cm = bulkValue === '' ? null : Number(bulkValue);
        const { error } = await supabase.from('sourcing_item_origins').update(payload).in('id', originIds);
        if (error) throw error;
      } else {
        const payload: Record<string, unknown> = {};
        if (bulkField === 'internal_name') payload.internal_name = bulkValue?.trim() ?? '';
        else if (bulkField === 'sales_format') payload.sales_format = bulkValue ?? '';
        else if (bulkField === 'brand_id') payload.brand_id = bulkValue || null;
        else if (bulkField === 'status_id') payload.status_id = bulkValue || null;
        else return;
        const { error } = await supabase.from('sourcing_items').update(payload).in('id', ids);
        if (error) throw error;
      }
    },
    onSuccess: (_, __) => {
      const count = selectedIds.size;
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins_batch'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_families'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'item_family_category'] });
      setSelectedIds(new Set());
      setBulkValue('');
      toast({ title: 'Actualizado', description: `Se aplicó a ${count} item(s).` });
    },
    onError: (e) => {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'bulk_apply', bulk_field: bulkField });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al aplicar', variant: 'destructive' });
    },
  });

  /** Convert a product into an origin of another: copy its origins to target (as inactive), then delete the source product. */
  const convertToOriginOfMutation = useMutation({
    mutationFn: async ({ sourceItemId, targetItemId }: { sourceItemId: string; targetItemId: string }) => {
      const { data: sourceOrigins, error: fetchErr } = await supabase
        .from('sourcing_item_origins')
        .select('*')
        .eq('sourcing_item_id', sourceItemId);
      if (fetchErr) throw fetchErr;
      if (!sourceOrigins?.length) throw new Error('El producto no tiene orígenes para copiar.');

      // Uniqueness is (sourcing_item_id, origin_id, origin_port_id, supplier_id). Skip when that triple already exists on target.
      const { data: targetOrigins } = await supabase
        .from('sourcing_item_origins')
        .select('origin_id, origin_port_id, supplier_id')
        .eq('sourcing_item_id', targetItemId);
      const key = (r: { origin_id: string; origin_port_id?: string | null; supplier_id?: string | null }) =>
        `${r.origin_id}|${r.origin_port_id ?? ''}|${r.supplier_id ?? ''}`;
      const targetKeys = new Set((targetOrigins ?? []).map(key));

      let inserted = 0;
      for (const o of sourceOrigins) {
        if (targetKeys.has(key(o))) continue; // target already has this (origin + port + supplier), skip
        const { error: insertErr } = await supabase.from('sourcing_item_origins').insert({
          sourcing_item_id: targetItemId,
          origin_id: o.origin_id,
          origin_port_id: o.origin_port_id ?? null,
          supplier_id: o.supplier_id ?? null,
          cost_usd: o.cost_usd ?? null,
          cost_incoterm_id: o.cost_incoterm_id ?? null,
          loading_method_id: o.loading_method_id ?? null,
          recommended_container_type: o.recommended_container_type ?? null,
          box_length_cm: o.box_length_cm ?? null,
          box_width_cm: o.box_width_cm ?? null,
          box_height_cm: o.box_height_cm ?? null,
          units_per_box: o.units_per_box ?? 0,
          boxes_per_pallet: o.boxes_per_pallet ?? null,
          boxes_per_container: o.boxes_per_container ?? 0,
          gross_weight_per_box_kg: o.gross_weight_per_box_kg ?? 0,
          moq_value: o.moq_value ?? 0,
          moq_unit_id: o.moq_unit_id ?? null,
          production_time_days: o.production_time_days ?? null,
          payment_terms: o.payment_terms ?? null,
          observaciones: o.observaciones ?? null,
          internal_notes: o.internal_notes ?? null,
          referencia: o.referencia ?? null,
          is_active: false, // new origins from merged product are secondary (not active)
        });
        if (insertErr) throw insertErr;
        inserted += 1;
        targetKeys.add(key(o));
      }
      if (inserted === 0 && sourceOrigins.length > 0) {
        throw new Error(
          'El producto origen tiene las mismas combinaciones (país/puerto/proveedor) que el destino. No se añadió ninguno.'
        );
      }
      // Remove source product images from Storage before deleting the row.
      await postWorkerApi<{ ok?: boolean }>('/api/delete-product-storage-images', { itemId: sourceItemId });
      const { error: delOriginsErr } = await supabase.from('sourcing_item_origins').delete().eq('sourcing_item_id', sourceItemId);
      if (delOriginsErr) throw delOriginsErr;
      const { error: delFamiliesErr } = await supabase.from('sourcing_item_families').delete().eq('item_id', sourceItemId);
      if (delFamiliesErr) throw delFamiliesErr;
      const { error: delItemErr } = await supabase.from('sourcing_items').delete().eq('id', sourceItemId);
      if (delItemErr) throw delItemErr;
    },
    onSuccess: (_, { targetItemId }) => {
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins', targetItemId] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_families'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'item_family_category'] });
      setConvertDialogOpen(false);
      setConvertSourceItem(null);
      toast({ title: 'Producto convertido', description: 'Los orígenes se añadieron al producto destino como orígenes secundarios. El producto duplicado fue eliminado.' });
    },
    onError: (e, variables) => {
      reportCaughtError(e, {
        screen: 'admin_sourcing_items',
        action: 'convert_to_origin_of',
        source_item_id: variables?.sourceItemId,
        target_item_id: variables?.targetItemId,
      });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al convertir', variant: 'destructive' });
    },
  });

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    const allPageSelected = paginatedItems.length > 0 && paginatedItems.every((i) => selectedIds.has(i.id));
    if (allPageSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(paginatedItems.map((i) => i.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  /** Build origin update payload from edit-origin form (for saving to selected origin when editing) */
  function buildOriginPayload(): Record<string, unknown> {
    return {
      origin_port_id: editOrigin_port_id || null,
      supplier_id: editOrigin_supplier_id || null,
      cost_usd: editOrigin_cost_usd ? Number(editOrigin_cost_usd) : null,
      cost_incoterm_id: editOrigin_cost_incoterm_id || null,
      units_per_box: Number(editOrigin_units_per_box) || 0,
      boxes_per_pallet: editOrigin_boxes_per_pallet ? Number(editOrigin_boxes_per_pallet) : null,
      boxes_per_container: Number(editOrigin_boxes_per_container) || 0,
      gross_weight_per_box_kg: Number(editOrigin_gross_weight_per_box_kg) || 0,
      moq_value: Number(editOrigin_moq_value) || 0,
      moq_unit_id: editOrigin_moq_unit_id || null,
      production_time_days: editOrigin_production_time_days ? Number(editOrigin_production_time_days) : null,
      payment_terms: editOrigin_payment_terms || null,
      observaciones: editOrigin_observaciones || null,
      internal_notes: editOrigin_internal_notes?.trim() || null,
      referencia: editOrigin_referencia?.trim() || null,
      loading_method_id: editOrigin_loading_method_id || null,
      recommended_container_type: editOrigin_recommended_container_type?.trim() || null,
      box_length_cm: editOrigin_box_length_cm === '' ? null : Number(editOrigin_box_length_cm),
      box_width_cm: editOrigin_box_width_cm === '' ? null : Number(editOrigin_box_width_cm),
      box_height_cm: editOrigin_box_height_cm === '' ? null : Number(editOrigin_box_height_cm),
    };
  }

  function resetForm() {
    setInternal_name('');
    setSlug('');
    setBrand_id(brands?.[0]?.id ?? '');
    setSales_format('');
    setPackshot_image('');
    setSecondary_images([]);
    setBarcode_ean('');
    setStatus_id(statuses?.[0]?.id ?? '');
    setFamily_id('');
    setEditing(null);
    setSelectedOriginId(null);
    setFirstOrigin_id(allOrigins?.[0]?.id ?? '');
    setFirstOrigin_port_id('');
    setFirstSupplier_id('');
    setFirstCost_usd('');
    setFirstCost_incoterm_id(costIncoterms?.[0]?.id ?? '');
    setFirstUnits_per_box('');
    setFirstBoxes_per_pallet('');
    setFirstBoxes_per_container('');
    setFirstGross_weight_per_box_kg('');
    setFirstMoq_value('');
    setFirstMoq_unit_id(moqUnits?.[0]?.id ?? '');
    setFirstProduction_time_days('');
    setFirstPayment_terms('');
    setFirstObservaciones('');
    setFirstInternal_notes('');
    setFirstReferencia('');
    setFirstLoading_method_id('');
    setFirstRecommended_container_type('');
    setFirstBox_length_cm('');
    setFirstBox_width_cm('');
    setFirstBox_height_cm('');
    setEditOrigin_loading_method_id('');
    setEditOrigin_recommended_container_type('');
    setEditOrigin_box_length_cm('');
    setEditOrigin_box_width_cm('');
    setEditOrigin_box_height_cm('');
    setSeoMetaTitle('');
    setSeoMetaDescription('');
    setSeoAssignedPorts('');
    setSeoSectionOpen(false);
  }

  async function openEdit(item: SourcingItem) {
    setEditing(item);
    setInternal_name(item.internal_name);
    setSlug(item.slug);
    setBrand_id(item.brand_id);
    setSales_format(item.sales_format);
    setPackshot_image(item.packshot_image ?? '');
    setSecondary_images(item.secondary_images ?? []);
    setBarcode_ean(item.barcode_ean ?? '');
    setStatus_id(item.status_id);
    // pSEO fields from seo_metadata JSONB
    setSeoMetaTitle(item.seo_metadata?.meta_title ?? '');
    setSeoMetaDescription(item.seo_metadata?.meta_description ?? '');
    setSeoAssignedPorts((item.seo_metadata?.assigned_ports ?? []).join(', '));
    setSeoSectionOpen(false);
    const { data: itemFamily } = await supabase
      .from('sourcing_item_families')
      .select('family_id')
      .eq('item_id', item.id)
      .limit(1)
      .maybeSingle();
    setFamily_id(itemFamily?.family_id ?? '');
    setOpen(true);
  }

  function openOrigins(item: SourcingItem) {
    setSelectedItem(item);
    setOriginsOpen(true);
  }

  /** Open the generate-image options modal (from edit dialog or from table row). */
  function openGenerateImageModal(item: SourcingItem) {
    if (!item?.id) return;
    setGenerateImageModalItem(item);
    setGenTarget('packshot');
    setGenImageType('clean');
    setGenReferenceUrl('');
    setGenIncludeBrand(true);
    setGenPrompt('');
    setGenPromptLoaded(false);
    setGenerateImageModalOpen(true);
  }

  /** Load prompt preview (Ver instrucción) without generating. */
  async function loadGenerateInstruction() {
    const item = generateImageModalItem;
    if (!item?.id) return;
    setGenLoadingPrompt(true);
    try {
      const result = await postWorkerApi<{ prompt?: string }>('/api/generate-catalog-image', {
        type: 'product',
        id: item.id,
        target: genTarget,
        imageType: genImageType,
        referenceImageUrl: genReferenceUrl.trim() || undefined,
        includeBrand: genIncludeBrand,
      });
      if (!result.ok) throw new Error(result.error || `HTTP ${result.status}`);
      setGenPrompt(result.data.prompt ?? '');
      setGenPromptLoaded(true);
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al cargar la instrucción', variant: 'destructive' });
    } finally {
      setGenLoadingPrompt(false);
    }
  }

  /** Build request body for generate-catalog-image (product) from current modal options. */
  function buildGenerateBody(confirm: boolean, background?: boolean) {
    const item = generateImageModalItem;
    if (!item?.id) return null;
    return {
      type: 'product' as const,
      id: item.id,
      confirm,
      background: !!background,
      target: genTarget,
      imageType: genImageType,
      referenceImageUrl: genReferenceUrl.trim() || undefined,
      includeBrand: genIncludeBrand,
      promptOverride: genPrompt.trim() || undefined,
    };
  }

  /** Generate image with current options (sync). */
  async function confirmGenerateImage() {
    const item = generateImageModalItem;
    const body = buildGenerateBody(true);
    if (!item?.id || !body) return;
    setGenGenerating(true);
    setGeneratingOneIds((prev) => new Set(prev).add(item.id));
    try {
      const result = await postWorkerApi<{ imageUrl?: string }>('/api/generate-catalog-image', body);
      if (!result.ok) throw new Error(result.error || `HTTP ${result.status}`);
      const imageUrl = result.data.imageUrl;
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      setGenerateImageModalOpen(false);
      setGenerateImageModalItem(null);
      if (genTarget === 'packshot' && editing?.id === item.id && imageUrl) {
        setPackshot_image(imageUrl);
        setPackshotPreviewKey((k) => k + 1);
      }
      toast({
        title: genTarget === 'packshot' ? 'Imagen generada' : 'Imagen añadida',
        description: genTarget === 'packshot' ? 'Portada actualizada.' : 'Se añadió a imágenes secundarias.',
      });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al generar', variant: 'destructive' });
    } finally {
      setGenGenerating(false);
      setGeneratingOneIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }

  /** Generate image in background (202). */
  async function confirmGenerateImageBackground() {
    const item = generateImageModalItem;
    const body = buildGenerateBody(true, true);
    if (!item?.id || !body) return;
    setGenGenerating(true);
    setGeneratingOneIds((prev) => new Set(prev).add(item.id));
    try {
      const result = await postWorkerApi<{ imageUrl?: string; message?: string }>('/api/generate-catalog-image', body);
      if (!result.ok) throw new Error(result.error || `HTTP ${result.status}`);
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      setGenerateImageModalOpen(false);
      setGenerateImageModalItem(null);
      if (result.status === 202) {
        if (genTarget === 'packshot' && editing?.id === item.id) {
          setPackshot_image(result.data.imageUrl ?? '');
          setPackshotPreviewKey((k) => k + 1);
        }
        toast({
          title: 'Generación en segundo plano',
          description: result.data.message ?? 'La imagen se actualizará en unos segundos.',
        });
      } else {
        if (genTarget === 'packshot' && editing?.id === item.id && result.data.imageUrl) {
          setPackshot_image(result.data.imageUrl);
          setPackshotPreviewKey((k) => k + 1);
        }
        toast({ title: 'Imagen generada', description: genTarget === 'packshot' ? 'Portada actualizada.' : 'Añadida a secundarias.' });
      }
      if (result.status === 202) {
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] }), 15000);
      }
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al generar', variant: 'destructive' });
    } finally {
      setGenGenerating(false);
      setGeneratingOneIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }

  async function handleBatchGenerate() {
    setBatchGenerating(true);
    try {
      const result = await postWorkerApi<{ generated?: number; message?: string; errors?: { id: string; message: string }[] }>(
        '/api/generate-catalog-image-batch',
        { type: 'product', limit: 6 }
      );
      if (!result.ok) throw new Error(result.error || `HTTP ${result.status}`);
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      const data = result.data;
      const isBackground = result.status === 202;
      const generated = data.generated ?? 0;
      const description = isBackground
        ? (data.message ?? 'Las imágenes se crearán en segundo plano. Puedes cerrar esta pestaña.')
        : (generated === 0 && data.message
          ? data.message
          : `Generadas ${generated} imagen(es).${data.errors?.length ? ` Errores: ${data.errors.map((e) => e.message).join('; ')}` : ''}`);
      toast({
        title: isBackground ? 'Generación en segundo plano' : generated === 0 ? 'Sin imágenes por generar' : 'Generación por lotes',
        description,
      });
    } catch (e) {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'batch_generate_images' });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al iniciar generación', variant: 'destructive' });
    } finally {
      setBatchGenerating(false);
    }
  }

  const q = safeTrim(searchQuery);
  const { data: itemFamilies } = useQuery({
    queryKey: ['store', 'sourcing_item_families', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase.from('sourcing_item_families').select('item_id, family_id');
      if (error) throw error;
      return (data ?? []) as Array<{ item_id: string; family_id: string }>;
    },
    enabled: true,
  });
  const itemIdsByFamily = (itemFamilies ?? []).reduce((acc, row) => {
    const set = acc.get(row.family_id) ?? new Set<string>();
    set.add(row.item_id);
    acc.set(row.family_id, set);
    return acc;
  }, new Map<string, Set<string>>());
  const familyFiltered = filterFamilyId && itemIdsByFamily.has(filterFamilyId)
    ? (itemIdsByFamily.get(filterFamilyId)! as Set<string>)
    : null;

  const hasPseo = (item: SourcingItem) =>
    !!(item.seo_metadata?.meta_title && item.seo_metadata?.meta_description);

  const filteredItems = (() => {
    let list = items ?? [];
    if (q) {
      list = list.filter(
        (item) =>
          searchMatch(item.internal_name, q) ||
          searchMatch(item.sales_format, q) ||
          searchMatch(item.slug, q)
      );
    }
    if (familyFiltered) {
      list = list.filter((item) => familyFiltered.has(item.id));
    }
    if (filterPseo === 'with') {
      list = list.filter(hasPseo);
    } else if (filterPseo === 'without') {
      list = list.filter((item) => !hasPseo(item));
    } else if (filterPseo === 'outdated') {
      list = list.filter((item) => hasPseo(item) && (item.seo_metadata?.pseo_version ?? 0) < currentPseoVersion);
    }
    return list;
  })();

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE) || 1;
  const paginatedItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const pageItemIds = useMemo(() => paginatedItems.map((i) => i.id), [paginatedItems]);
  const { data: pageOrigins } = useQuery({
    queryKey: ['store', 'sourcing_item_origins_batch', pageItemIds.slice().sort().join(',')],
    enabled: pageItemIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('sourcing_item_origins').select('*').in('sourcing_item_id', pageItemIds);
      if (error) throw error;
      return (data ?? []) as SourcingItemOrigin[];
    },
  });
  const originsByItemId = useMemo(() => {
    const m = new Map<string, SourcingItemOrigin[]>();
    for (const o of pageOrigins ?? []) {
      const list = m.get(o.sourcing_item_id) ?? [];
      list.push(o);
      m.set(o.sourcing_item_id, list);
    }
    return m;
  }, [pageOrigins]);

  /** Items without packshot (null or empty) — batch generation only processes these. */
  const itemsWithoutImage = (items ?? []).filter(
    (i) => !i.packshot_image || String(i.packshot_image).trim() === ''
  );

  /** Items that need pSEO: either missing entirely or running an outdated version. */
  const itemsNeedingSeo = (items ?? []).filter(
    (i) => !hasPseo(i) || (i.seo_metadata?.pseo_version ?? 0) < currentPseoVersion,
  );

  /** Regenerate pSEO for the currently edited item via AI. */
  async function handleRegenerateSeoForItem() {
    if (!editing) return;
    setSeoRegenerating(true);
    try {
      const result = await postWorkerApi<{ generated?: number; errors?: Array<{ id: string; error?: string }> }>(
        '/api/generate-seo-metadata-batch',
        { ids: [editing.id] }
      );
      if (!result.ok) throw new Error(result.error || `HTTP ${result.status}`);
      toast({ title: 'pSEO generado', description: `Metadatos SEO regenerados para "${editing.internal_name}".` });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      // Reload form fields from fresh data after a short delay
      setTimeout(async () => {
        const { data } = await supabase.from('sourcing_items').select('seo_metadata,slug').eq('id', editing.id).single();
        if (data) {
          setSeoMetaTitle(data.seo_metadata?.meta_title ?? '');
          setSeoMetaDescription(data.seo_metadata?.meta_description ?? '');
          setSeoAssignedPorts((data.seo_metadata?.assigned_ports ?? []).join(', '));
          setSlug(data.slug ?? '');
        }
      }, 2000);
    } catch (e) {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'regenerate_seo_item' });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al regenerar pSEO', variant: 'destructive' });
    } finally {
      setSeoRegenerating(false);
    }
  }

  /** Chunk size for batch pSEO (must match CHUNK_PER_CALL in seo-generate.ts). Sending small chunks avoids URL-too-long. */
  const SEO_BATCH_CHUNK = 20;

  /**
   * Batch pSEO: calls the worker in chunks. When ids are provided we send SEO_BATCH_CHUNK at a time
   * and loop until all are done. When no ids we use worker "all missing" path and loop by remaining.
   */
  async function handleBatchSeoGenerate(ids?: string[]) {
    setBatchSeoGenerating(true);
    setBatchSeoProgress('Iniciando…');
    let totalGenerated = 0;
    let totalFailed = 0;
    let idOffset = 0;
    const totalIds = ids?.length ?? 0;
    try {
      let remaining = 1;
      while (remaining > 0) {
        const body: Record<string, unknown> = {};
        if (ids && ids.length > 0) {
          const chunk = ids.slice(idOffset, idOffset + SEO_BATCH_CHUNK);
          if (chunk.length === 0) break;
          body.ids = chunk;
        }
        if (upgradeSlugWhenGenerating) {
          body.upgrade_slug = true;
        }
        const result = await postWorkerApi<{
          generated?: number; failed?: number; remaining?: number; total?: number;
          message?: string; errors?: Array<{ id: string; error?: string }>;
        }>('/api/generate-seo-metadata-batch', body);

        if (!result.ok) throw new Error(result.error || `HTTP ${result.status}`);

        const generated = result.data.generated ?? 0;
        totalGenerated += generated;
        totalFailed += result.data.failed ?? 0;
        if (ids && ids.length > 0) {
          idOffset += (body.ids as string[]).length;
          remaining = idOffset < ids.length ? 1 : 0;
        } else {
          remaining = result.data.remaining ?? 0;
        }

        const restantes = ids && ids.length > 0 ? totalIds - idOffset : remaining;
        setBatchSeoProgress(`${totalGenerated} generados${totalFailed ? `, ${totalFailed} errores` : ''}${remaining > 0 ? `, ${restantes} restantes…` : ''}`);

        queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
      }

      toast({
        title: 'pSEO completado',
        description: `${totalGenerated} generados${totalFailed ? `, ${totalFailed} errores` : ''}. Lista actualizada.`,
      });
    } catch (e) {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'batch_generate_seo' });
      toast({
        title: 'Error en generación pSEO',
        description: `${totalGenerated} generados antes del error. ${e instanceof Error ? e.message : 'Error desconocido'}`,
        variant: 'destructive',
      });
    } finally {
      setBatchSeoGenerating(false);
      setBatchSeoProgress('');
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Sourcing items</h1>
      <p className="text-muted-foreground text-sm mb-2">
        Each item can have multiple origins. Click "Origins" to manage logistics per origin.
      </p>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Buscar por nombre, formato o slug..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">Familia</Label>
          <select
            className="h-9 rounded-md border bg-background px-3 min-w-[180px] text-sm"
            value={filterFamilyId}
            onChange={(e) => { setFilterFamilyId(e.target.value); setPage(1); }}
            aria-label="Filtrar por familia"
          >
            <option value="">Todas las familias</option>
            {(families ?? []).map((f) => (
              <option key={f.id} value={f.id}>{f.family_name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">pSEO</Label>
          <select
            className="h-9 rounded-md border bg-background px-3 min-w-[130px] text-sm"
            value={filterPseo}
            onChange={(e) => { setFilterPseo(e.target.value as 'all' | 'with' | 'without' | 'outdated'); setPage(1); }}
            aria-label="Filtrar por pSEO"
          >
            <option value="all">Todos</option>
            <option value="with">Con pSEO</option>
            <option value="without">Sin pSEO</option>
            <option value="outdated">Versión antigua</option>
          </select>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setEditing(null); }}>Add item</Button>
          </DialogTrigger>
        </Dialog>
        <Button
          type="button"
          variant="outline"
          onClick={handleBatchGenerate}
          disabled={batchGenerating || itemsWithoutImage.length === 0}
          title={itemsWithoutImage.length === 0 ? 'Todos los productos tienen ya imagen de portada' : `Generar imagen para ${itemsWithoutImage.length} producto(s) sin imagen`}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {batchGenerating ? 'Iniciando...' : itemsWithoutImage.length === 0
            ? 'Todos tienen imagen'
            : `Generar imágenes faltantes (${itemsWithoutImage.length} sin imagen)`}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => handleBatchSeoGenerate(itemsNeedingSeo.map(i => i.id))}
          disabled={batchSeoGenerating || itemsNeedingSeo.length === 0}
          title={itemsNeedingSeo.length === 0 ? `Todos los productos tienen pSEO v${currentPseoVersion}` : `Generar/actualizar pSEO para ${itemsNeedingSeo.length} producto(s)`}
        >
          <Search className="h-4 w-4 mr-2" />
          {batchSeoGenerating
            ? (batchSeoProgress || 'Procesando pSEO…')
            : itemsNeedingSeo.length === 0
              ? `Todos en v${currentPseoVersion}`
              : `Generar pSEO faltante/antiguo (${itemsNeedingSeo.length})`}
        </Button>
        {selectedIds.size > 0 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => handleBatchSeoGenerate(Array.from(selectedIds))}
            disabled={batchSeoGenerating}
          >
            <Search className="h-4 w-4 mr-2" />
            {batchSeoGenerating ? 'Iniciando pSEO...' : `Generar pSEO para ${selectedIds.size} seleccionados`}
          </Button>
        )}
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <Checkbox
            checked={upgradeSlugWhenGenerating}
            onCheckedChange={(c) => setUpgradeSlugWhenGenerating(!!c)}
            disabled={batchSeoGenerating}
            aria-describedby="upgrade-slug-desc"
          />
          <span id="upgrade-slug-desc">Actualizar slug a la nueva versión (301 desde el actual)</span>
        </label>
      </div>
      {safeTrim(searchQuery) && (
        <p className="text-sm text-muted-foreground mb-2">
          {filteredItems.length} de {(items ?? []).length} resultado(s)
        </p>
      )}

      {filteredItems.length > 0 && (
        <div className="rounded-lg border bg-muted/50 mb-4 overflow-hidden">
          <div className="px-3 py-2 bg-zinc-100 border-b border-zinc-200">
            <span className="text-sm font-semibold text-zinc-800">Edición masiva</span>
            <span className="text-xs text-muted-foreground ml-2">Marca filas con «Sel.» y elige el parámetro + valor. Los del grupo «Origen activo» se aplican al origen marcado de cada ítem.</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 p-3">
            {selectedIds.size > 0 ? (
              <>
                <span className="text-sm font-medium shrink-0">{selectedIds.size} item(s) seleccionado(s)</span>
                <Label className="sr-only">Parámetro a modificar</Label>
                <select
                  className="h-9 rounded-md border bg-background px-3 min-w-[220px] text-sm font-medium"
                  value={bulkField}
                  onChange={(e) => { setBulkField(e.target.value); setBulkValue(''); }}
                  aria-label="Parámetro a modificar"
                >
                  <optgroup label="—— Producto ——">
                    <option value="internal_name">Nombre</option>
                    <option value="sales_format">Formato</option>
                    <option value="brand_id">Marca</option>
                    <option value="status_id">Estado</option>
                    <option value="family_id">Familia</option>
                  </optgroup>
                  <optgroup label="—— Origen activo (el marcado por ítem) ——">
                    <option value="origin_id">Origen (país)</option>
                    <option value="origin_port_id">Puerto origen</option>
                    <option value="supplier_id">Proveedor</option>
                    <option value="loading_method_id">Método de carga</option>
                    <option value="recommended_container_type">Contenedor recomendado</option>
                    <option value="box_length_cm">Largo caja (cm)</option>
                    <option value="box_width_cm">Ancho caja (cm)</option>
                    <option value="box_height_cm">Alto caja (cm)</option>
                    <option value="cost_usd">Coste USD</option>
                    <option value="cost_incoterm_id">Incoterm coste</option>
                    <option value="units_per_box">Unidades por caja</option>
                    <option value="boxes_per_pallet">Cajas por pallet</option>
                    <option value="boxes_per_container">Cajas por contenedor</option>
                    <option value="gross_weight_per_box_kg">Peso bruto/caja (kg)</option>
                    <option value="moq_value">MOQ valor</option>
                    <option value="moq_unit_id">MOQ unidad</option>
                    <option value="production_time_days">Días producción</option>
                    <option value="payment_terms">Condiciones de pago</option>
                    <option value="referencia">Referencia (código proveedor)</option>
                    <option value="observaciones">Observaciones</option>
                    <option value="internal_notes">Info. interna</option>
                  </optgroup>
                </select>
                {/* Valor según el parámetro elegido */}
                {(bulkField === 'internal_name') && (
                  <Input className="h-9 w-[240px]" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Nombre del producto" />
                )}
                {(bulkField === 'sales_format') && (
                  <Input className="h-9 w-[200px]" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Formato" />
                )}
                {(bulkField === 'brand_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin marca —</option>
                    {(brands ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                )}
                {(bulkField === 'status_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin estado —</option>
                    {(statuses ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
                {(bulkField === 'family_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin familia —</option>
                    {(families ?? []).map((f) => <option key={f.id} value={f.id}>{f.family_name}</option>)}
                  </select>
                )}
                {(bulkField === 'origin_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin cambio —</option>
                    {(allOrigins ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                )}
                {(bulkField === 'origin_port_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[220px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin puerto / limpiar —</option>
                    {(allOriginPorts ?? []).map((p) => {
                      const orig = allOrigins?.find((o) => o.id === p.origin_id);
                      return <option key={p.id} value={p.id}>{p.name}{orig ? ` (${orig.name})` : ''}</option>;
                    })}
                  </select>
                )}
                {(bulkField === 'supplier_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin proveedor —</option>
                    {(suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
                {(bulkField === 'loading_method_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin método —</option>
                    {(loadingMethods ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                )}
                {(bulkField === 'recommended_container_type') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin cambio / limpiar —</option>
                    <option value="20DV">20ft Dry</option>
                    <option value="40HQ">40ft Dry</option>
                    <option value="20REF">20ft Refer</option>
                    <option value="40REF">40ft Refer</option>
                  </select>
                )}
                {(bulkField === 'cost_incoterm_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin incoterm —</option>
                    {(costIncoterms ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
                {(bulkField === 'moq_unit_id') && (
                  <select className="h-9 rounded-md border bg-background px-3 w-[200px] text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                    <option value="">— Sin unidad —</option>
                    {(moqUnits ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                )}
                {['cost_usd', 'units_per_box', 'boxes_per_pallet', 'boxes_per_container', 'gross_weight_per_box_kg', 'moq_value', 'production_time_days', 'box_length_cm', 'box_width_cm', 'box_height_cm'].includes(bulkField) && (
                  <Input
                    type="number"
                    className="h-9 w-[140px]"
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    placeholder={bulkField === 'cost_usd' ? 'Ej. 1.50' : bulkField === 'production_time_days' ? 'Días' : bulkField.startsWith('box_') ? 'cm' : 'Número'}
                  />
                )}
                                {(bulkField === 'payment_terms' || bulkField === 'referencia' || bulkField === 'internal_notes') && (
                  <Input className="h-9 w-[240px]" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder={bulkField === 'referencia' ? 'Código proveedor' : bulkField === 'internal_notes' ? 'Info. interna' : 'Texto'} />
                )}
                {(bulkField === 'observaciones') && (
                  <Textarea className="min-w-[200px] max-w-[320px] min-h-9 text-sm" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Observaciones" rows={1} />
                )}
                <Button type="button" size="sm" onClick={() => bulkApplyMutation.mutate()} disabled={bulkApplyMutation.isPending}>
                  {bulkApplyMutation.isPending ? 'Aplicando…' : 'Aplicar a seleccionados'}
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => setOpenNormalizeLoadModal(true)} title="Completar datos de carga (units_per_box, peso, etc.) con IA para los orígenes activos">
                  <Sparkles className="h-4 w-4 mr-1" />
                  Completar datos de carga con IA
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                  Desmarcar todas
                </Button>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Marca filas con la casilla «Sel.» (primera columna) para activar la edición masiva.</span>
            )}
          </div>
        </div>
      )}

      {/* Fase 4: Completar datos de carga con IA (orígenes activos de los ítems seleccionados) */}
      <Dialog open={openNormalizeLoadModal} onOpenChange={setOpenNormalizeLoadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Completar datos de carga con IA
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Se procesará el <strong>origen activo</strong> de cada ítem seleccionado ({selectedIds.size}). La IA rellenará solo campos vacíos (units_per_box, peso, cajas por contenedor, etc.).
          </p>
          <div>
            <Label>Instrucción adicional (opcional)</Label>
            <Textarea
              className="mt-1"
              value={normalizeLoadInstruction}
              onChange={(e) => setNormalizeLoadInstruction(e.target.value)}
              placeholder="Ej: priorizar peso por caja y pallets por contenedor"
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpenNormalizeLoadModal(false)}>Cancelar</Button>
            <Button
              disabled={normalizeLoadLoading}
              onClick={async () => {
                setNormalizeLoadLoading(true);
                try {
                  const { data: origins } = await supabase
                    .from('sourcing_item_origins')
                    .select('id')
                    .in('sourcing_item_id', Array.from(selectedIds))
                    .eq('is_active', true);
                  const originIds = (origins ?? []).map((o) => o.id);
                  if (originIds.length === 0) {
                    toast({ title: 'Sin orígenes', description: 'Ningún ítem seleccionado tiene origen activo.', variant: 'destructive' });
                    return;
                  }
                  const res = await postWorkerApi<{ results?: Array<{ origin_id: string; success: boolean; error?: string }>; message?: string }>(
                    '/api/normalize-origin-load-data',
                    { origin_ids: originIds, instruction: normalizeLoadInstruction || undefined, background: true }
                  );
                  if (!res.ok) {
                    toast({ title: 'Error', description: res.error ?? 'Error al llamar al servicio', variant: 'destructive' });
                    return;
                  }
                  queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
                  queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins'] });
                  setOpenNormalizeLoadModal(false);
                  setNormalizeLoadInstruction('');
                  if (res.status === 202) {
                    toast({ title: 'En segundo plano', description: res.data?.message ?? 'Completando datos de carga con IA. Puedes seguir trabajando; los datos se actualizarán en unos segundos.' });
                    setTimeout(() => queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] }), 15000);
                    setTimeout(() => queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins'] }), 15000);
                  } else {
                    const ok = res.data?.results?.filter((r) => r.success).length ?? 0;
                    const fail = (res.data?.results?.length ?? 0) - ok;
                    toast({ title: 'Completado', description: `${ok} origen(es) actualizado(s)${fail > 0 ? `; ${fail} error(es).` : '.'}` });
                  }
                } catch (e) {
                  reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'normalize_load_data' });
                  toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error', variant: 'destructive' });
                } finally {
                  setNormalizeLoadLoading(false);
                }
              }}
            >
              {normalizeLoadLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Ejecutar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogTrigger asChild>
          <span className="sr-only">open</span>
        </DialogTrigger>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit item' : 'New item'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div><Label>Internal name</Label><Input value={internal_name} onChange={(e) => setInternal_name(e.target.value)} /></div>
            <div><Label>Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} /></div>
            <div><Label>Brand</Label>
              <select className="w-full border rounded px-3 py-2" value={brand_id} onChange={(e) => setBrand_id(e.target.value)}>
                {(brands ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div><Label>Sales format</Label><Input value={sales_format} onChange={(e) => setSales_format(e.target.value)} /></div>
            <div>
              <Label>Familia (opcional)</Label>
              <select className="w-full border rounded px-3 py-2" value={family_id} onChange={(e) => setFamily_id(e.target.value)}>
                <option value="">— Sin familia —</option>
                {(families ?? []).map((f) => (
                  <option key={f.id} value={f.id}>{f.family_name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Determina en qué familia aparece el producto en la tienda.</p>
            </div>
            {/* Images section: cover, generate, secondaries — grouped for clearer layout */}
            <div className="space-y-4 rounded-lg border border-zinc-200 bg-zinc-50/50 p-4">
              <h3 className="font-semibold text-zinc-900 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Imágenes del producto
              </h3>
              <div className="grid gap-4 sm:grid-cols-[1fr,auto]">
                <div className="space-y-2 min-w-0">
                  <ImageUploadField label="Portada actual (packshot)" value={packshot_image} onChange={setPackshot_image} folder="items" previewKey={packshotPreviewKey} />
                  <p className="text-muted-foreground text-xs">El thumbnail en listados se genera automáticamente desde la portada. En secundarias puedes usar «Usar como portada» para cambiar la principal.</p>
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editing?.id && openGenerateImageModal(editing)}
                    disabled={!editing?.id}
                    title={!editing?.id ? 'Guardar el producto primero; luego edítalo para generar la imagen con IA' : undefined}
                    className="shrink-0"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generar imagen con IA
                  </Button>
                  {!editing?.id && (
                    <span className="text-xs text-muted-foreground">Disponible al editar (guardar primero)</span>
                  )}
                </div>
              </div>
              <SecondaryImagesList
                value={secondary_images}
                onChange={setSecondary_images}
                folder="items"
                onSetAsCover={(url) => {
                  setPackshot_image(url);
                  setSecondary_images((prev) => {
                    const without = prev.filter((u) => u !== url);
                    if (packshot_image?.trim()) return [...without, packshot_image];
                    return without;
                  });
                  setPackshotPreviewKey((k) => k + 1);
                }}
              />
            </div>
            <div><Label>Barcode EAN (hidden)</Label><Input value={barcode_ean} onChange={(e) => setBarcode_ean(e.target.value)} /></div>
            <div><Label>Status</Label>
              <select className="w-full border rounded px-3 py-2" value={status_id} onChange={(e) => setStatus_id(e.target.value)}>
                {(statuses ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* When editing: show selected origin and its data so changes apply to that origin */}
            {editing && (
              <>
                <hr className="border-zinc-200 my-6" />
                <h3 className="font-semibold text-zinc-900 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Datos del origen seleccionado
                </h3>
                <p className="text-sm text-muted-foreground">
                  Los cambios en los campos de abajo se guardan en el origen que elijas. Si hay un origen activo, se muestra por defecto.
                </p>
                {!itemOrigins?.length ? (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Este producto aún no tiene orígenes. Añade al menos uno desde el botón «Origins» en la tabla y vuelve a editar.
                  </p>
                ) : (
                  <>
                    <div className="pt-2">
                      <Label>Origen a editar</Label>
                      <select
                        className="w-full border rounded px-3 py-2 mt-1"
                        value={selectedOriginId ?? ''}
                        onChange={(e) => setSelectedOriginId(e.target.value || null)}
                      >
                        {(itemOrigins ?? []).map((o) => {
                          const name = allOrigins?.find((x) => x.id === o.origin_id)?.name ?? o.origin_id;
                          const port = allOriginPorts?.find((p) => p.id === o.origin_port_id)?.name;
                          const label = port ? `${name} – ${port}` : name;
                          return (
                            <option key={o.id} value={o.id}>
                              {label} {o.is_active ? '(Activo)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {selectedOriginId && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                        <div>
                          <Label>Puerto</Label>
                          <select className="w-full border rounded px-3 py-2" value={editOrigin_port_id} onChange={(e) => setEditOrigin_port_id(e.target.value)}>
                            <option value="">— Opcional —</option>
                            {(editOriginPorts ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label>Proveedor</Label>
                          <select className="w-full border rounded px-3 py-2" value={editOrigin_supplier_id} onChange={(e) => setEditOrigin_supplier_id(e.target.value)}>
                            <option value="">— Sin supplier —</option>
                            {(suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label>Coste USD por caja (interno)</Label>
                          <Input type="number" value={editOrigin_cost_usd} onChange={(e) => setEditOrigin_cost_usd(e.target.value)} placeholder="Opcional" />
                        </div>
                        <div>
                          <Label>Incoterm coste</Label>
                          <select className="w-full border rounded px-3 py-2" value={editOrigin_cost_incoterm_id} onChange={(e) => setEditOrigin_cost_incoterm_id(e.target.value)}>
                            <option value="">— Opcional —</option>
                            {(costIncoterms ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label>Unidades por caja</Label>
                          <Input type="number" value={editOrigin_units_per_box} onChange={(e) => setEditOrigin_units_per_box(e.target.value)} />
                        </div>
                        <div>
                          <Label>Cajas por pallet</Label>
                          <Input type="number" value={editOrigin_boxes_per_pallet} onChange={(e) => setEditOrigin_boxes_per_pallet(e.target.value)} placeholder="Opcional" />
                        </div>
                        <div>
                          <Label>Cajas por contenedor</Label>
                          <Input type="number" value={editOrigin_boxes_per_container} onChange={(e) => setEditOrigin_boxes_per_container(e.target.value)} />
                        </div>
                        <div>
                          <Label>Método de carga</Label>
                          <select className="w-full border rounded px-3 py-2" value={editOrigin_loading_method_id} onChange={(e) => setEditOrigin_loading_method_id(e.target.value)}>
                            <option value="">— Opcional —</option>
                            {(loadingMethods ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label>Contenedor recomendado</Label>
                          <select className="w-full border rounded px-3 py-2" value={editOrigin_recommended_container_type} onChange={(e) => setEditOrigin_recommended_container_type(e.target.value)}>
                            <option value="">— Opcional —</option>
                            <option value="20DV">20ft Dry</option>
                            <option value="40HQ">40ft Dry</option>
                            <option value="20REF">20ft Refer</option>
                            <option value="40REF">40ft Refer</option>
                          </select>
                        </div>
                        <div>
                          <Label>Peso bruto por caja (kg)</Label>
                          <Input type="number" value={editOrigin_gross_weight_per_box_kg} onChange={(e) => setEditOrigin_gross_weight_per_box_kg(e.target.value)} />
                        </div>
                        <div className="sm:col-span-2">
                          <Label>Dimensiones de caja (cm) — para llenado por espacio (carga a piso)</Label>
                          <div className="flex gap-2 items-center mt-1">
                            <Input type="number" min={0} placeholder="L" value={editOrigin_box_length_cm} onChange={(e) => setEditOrigin_box_length_cm(e.target.value)} className="w-20" />
                            <span className="text-zinc-400">×</span>
                            <Input type="number" min={0} placeholder="W" value={editOrigin_box_width_cm} onChange={(e) => setEditOrigin_box_width_cm(e.target.value)} className="w-20" />
                            <span className="text-zinc-400">×</span>
                            <Input type="number" min={0} placeholder="H" value={editOrigin_box_height_cm} onChange={(e) => setEditOrigin_box_height_cm(e.target.value)} className="w-20" />
                            <span className="text-xs text-zinc-500">L × W × H</span>
                          </div>
                        </div>
                        <div>
                          <Label>MOQ valor</Label>
                          <Input type="number" value={editOrigin_moq_value} onChange={(e) => setEditOrigin_moq_value(e.target.value)} />
                        </div>
                        <div>
                          <Label>MOQ unidad</Label>
                          <select className="w-full border rounded px-3 py-2" value={editOrigin_moq_unit_id} onChange={(e) => setEditOrigin_moq_unit_id(e.target.value)}>
                            <option value="">— Opcional —</option>
                            {(moqUnits ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label>Días de producción</Label>
                          <Input type="number" value={editOrigin_production_time_days} onChange={(e) => setEditOrigin_production_time_days(e.target.value)} placeholder="Opcional" />
                        </div>
                        <div>
                          <Label>Condiciones de pago</Label>
                          <Input value={editOrigin_payment_terms} onChange={(e) => setEditOrigin_payment_terms(e.target.value)} placeholder="Opcional" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label>Referencia (código proveedor)</Label>
                          <Input value={editOrigin_referencia} onChange={(e) => setEditOrigin_referencia(e.target.value)} placeholder="Opcional" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label>Observaciones</Label>
                          <textarea
                            className="w-full border rounded px-3 py-2 min-h-[80px]"
                            value={editOrigin_observaciones}
                            onChange={(e) => setEditOrigin_observaciones(e.target.value)}
                            placeholder="Opcional"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label>Info. interna (solo admin)</Label>
                          <textarea
                            className="w-full border rounded px-3 py-2 min-h-[60px] bg-amber-50/50"
                            value={editOrigin_internal_notes}
                            onChange={(e) => setEditOrigin_internal_notes(e.target.value)}
                            placeholder="Notas para compra/coste; no se muestran al cliente"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* First origin form: only when creating new item (required to save) */}
            {!editing && (
              <>
                <hr className="border-zinc-200 my-6" />
                <h3 className="font-semibold text-zinc-900">Primer origen (obligatorio)</h3>
                <p className="text-sm text-muted-foreground">Cada producto debe tener al menos un origen. Puedes añadir más después desde "Origins".</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label>Origen (país)</Label>
                    <select
                      className="w-full border rounded px-3 py-2"
                      value={firstOrigin_id}
                      onChange={(e) => { setFirstOrigin_id(e.target.value); setFirstOrigin_port_id(''); }}
                    >
                      <option value="">— Seleccionar —</option>
                      {(allOrigins ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Puerto</Label>
                    <select className="w-full border rounded px-3 py-2" value={firstOrigin_port_id} onChange={(e) => setFirstOrigin_port_id(e.target.value)}>
                      <option value="">— Opcional —</option>
                      {(firstOriginPorts ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Proveedor (opcional)</Label>
                    <select className="w-full border rounded px-3 py-2" value={firstSupplier_id} onChange={(e) => setFirstSupplier_id(e.target.value)}>
                      <option value="">— Sin supplier —</option>
                      {(suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Coste USD por caja (interno)</Label>
                    <Input type="number" value={firstCost_usd} onChange={(e) => setFirstCost_usd(e.target.value)} placeholder="Opcional" />
                  </div>
                  <div>
                    <Label>Incoterm coste</Label>
                    <select className="w-full border rounded px-3 py-2" value={firstCost_incoterm_id} onChange={(e) => setFirstCost_incoterm_id(e.target.value)}>
                      {(costIncoterms ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Unidades por caja</Label>
                    <Input type="number" value={firstUnits_per_box} onChange={(e) => setFirstUnits_per_box(e.target.value)} />
                  </div>
                  <div>
                    <Label>Cajas por pallet</Label>
                    <Input type="number" value={firstBoxes_per_pallet} onChange={(e) => setFirstBoxes_per_pallet(e.target.value)} placeholder="Opcional" />
                  </div>
                  <div>
                    <Label>Cajas por contenedor</Label>
                    <Input type="number" value={firstBoxes_per_container} onChange={(e) => setFirstBoxes_per_container(e.target.value)} />
                  </div>
                  <div>
                    <Label>Método de carga</Label>
                    <select className="w-full border rounded px-3 py-2" value={firstLoading_method_id} onChange={(e) => setFirstLoading_method_id(e.target.value)}>
                      <option value="">— Opcional —</option>
                      {(loadingMethods ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Contenedor recomendado</Label>
                    <select className="w-full border rounded px-3 py-2" value={firstRecommended_container_type} onChange={(e) => setFirstRecommended_container_type(e.target.value)}>
                      <option value="">— Opcional —</option>
                      <option value="20DV">20ft Dry</option>
                      <option value="40HQ">40ft Dry</option>
                      <option value="20REF">20ft Refer</option>
                      <option value="40REF">40ft Refer</option>
                    </select>
                  </div>
                  <div>
                    <Label>Peso bruto por caja (kg)</Label>
                    <Input type="number" value={firstGross_weight_per_box_kg} onChange={(e) => setFirstGross_weight_per_box_kg(e.target.value)} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Dimensiones de caja (cm) — L × W × H</Label>
                    <div className="flex gap-2 items-center mt-1">
                      <Input type="number" min={0} placeholder="L" value={firstBox_length_cm} onChange={(e) => setFirstBox_length_cm(e.target.value)} className="w-20" />
                      <span className="text-zinc-400">×</span>
                      <Input type="number" min={0} placeholder="W" value={firstBox_width_cm} onChange={(e) => setFirstBox_width_cm(e.target.value)} className="w-20" />
                      <span className="text-zinc-400">×</span>
                      <Input type="number" min={0} placeholder="H" value={firstBox_height_cm} onChange={(e) => setFirstBox_height_cm(e.target.value)} className="w-20" />
                    </div>
                  </div>
                  <div>
                    <Label>MOQ valor</Label>
                    <Input type="number" value={firstMoq_value} onChange={(e) => setFirstMoq_value(e.target.value)} />
                  </div>
                  <div>
                    <Label>MOQ unidad</Label>
                    <select className="w-full border rounded px-3 py-2" value={firstMoq_unit_id} onChange={(e) => setFirstMoq_unit_id(e.target.value)}>
                      {(moqUnits ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Días de producción</Label>
                    <Input type="number" value={firstProduction_time_days} onChange={(e) => setFirstProduction_time_days(e.target.value)} placeholder="Opcional" />
                  </div>
                  <div>
                    <Label>Condiciones de pago</Label>
                    <Input value={firstPayment_terms} onChange={(e) => setFirstPayment_terms(e.target.value)} placeholder="Opcional" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Referencia (código proveedor)</Label>
                    <Input value={firstReferencia} onChange={(e) => setFirstReferencia(e.target.value)} placeholder="Opcional" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Observaciones</Label>
                    <textarea
                      className="w-full border rounded px-3 py-2 min-h-[80px]"
                      value={firstObservaciones}
                      onChange={(e) => setFirstObservaciones(e.target.value)}
                      placeholder="Opcional"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Info. interna (solo admin)</Label>
                    <textarea
                      className="w-full border rounded px-3 py-2 min-h-[60px] bg-amber-50/50"
                      value={firstInternal_notes}
                      onChange={(e) => setFirstInternal_notes(e.target.value)}
                      placeholder="Notas para compra/coste; no al cliente"
                    />
                  </div>
                </div>
              </>
            )}

            {/* pSEO: collapsible section for programmatic SEO metadata */}
            <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 overflow-hidden">
              <button
                type="button"
                className="flex items-center gap-2 w-full px-4 py-3 text-left font-semibold text-zinc-900 hover:bg-zinc-100 transition-colors"
                onClick={() => setSeoSectionOpen((o) => !o)}
              >
                {seoSectionOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Search className="h-4 w-4" />
                SEO Programático (pSEO)
                {hasPseo(editing ?? {} as SourcingItem) && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-300 bg-green-50 text-green-700 ml-2">✓</Badge>
                )}
              </button>
              {seoSectionOpen && (
                <div className="px-4 pb-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      Texto no visible para el cliente. Se usa para title, meta description y JSON-LD.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRegenerateSeoForItem}
                      disabled={seoRegenerating || !editing}
                    >
                      <Search className="h-3.5 w-3.5 mr-1.5" />
                      {seoRegenerating ? 'Generando…' : hasPseo(editing ?? {} as SourcingItem) ? 'Rehacer pSEO' : 'Generar pSEO'}
                    </Button>
                  </div>
                  <div>
                    <Label>Meta Title</Label>
                    <Input
                      value={seoMetaTitle}
                      onChange={(e) => setSeoMetaTitle(e.target.value.slice(0, 60))}
                      maxLength={60}
                      placeholder="Ej: Comprar Producto X al por mayor – Importación B2B LATAM"
                    />
                    <p className="text-xs text-muted-foreground mt-1 text-right">{seoMetaTitle.length}/60</p>
                  </div>
                  <div>
                    <Label>Meta Description</Label>
                    <Textarea
                      value={seoMetaDescription}
                      onChange={(e) => setSeoMetaDescription(e.target.value.slice(0, 160))}
                      maxLength={160}
                      rows={3}
                      placeholder="Descripción breve para Google (~155 caracteres)"
                    />
                    <p className={`text-xs mt-1 text-right ${seoMetaDescription.length > 155 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                      {seoMetaDescription.length}/160{seoMetaDescription.length > 155 && ' ⚠ Google puede truncar'}
                    </p>
                  </div>
                  <div>
                    <Label>Puertos asignados (comma-separated)</Label>
                    <Textarea
                      value={seoAssignedPorts}
                      onChange={(e) => setSeoAssignedPorts(e.target.value)}
                      rows={2}
                      placeholder="Ej: Mariel, Callao, Manzanillo"
                    />
                  </div>
                  {/* SEO slug: read-only, generated by AI or set during batch processing */}
                  {editing?.seo_metadata?.seo_slug && (
                    <div>
                      <Label>SEO Slug</Label>
                      <Input
                        value={editing.seo_metadata.seo_slug}
                        readOnly
                        className="bg-zinc-100 text-muted-foreground font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Generado por IA. Se aplica como slug del producto al generar pSEO.</p>
                    </div>
                  )}
                  {/* Current live slug (what's actually in the URL) */}
                  <div>
                    <Label>Slug actual (URL)</Label>
                    <Input
                      value={slug}
                      readOnly
                      className="bg-zinc-100 text-muted-foreground font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label>Search Intent</Label>
                    <Input value="b2b_wholesale_latam" readOnly className="bg-zinc-100 text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={() => {
                if (editing && selectedOriginId) {
                  saveMutation.mutate({ originId: selectedOriginId, originPayload: buildOriginPayload() });
                } else {
                  saveMutation.mutate(undefined);
                }
              }}
              disabled={saveMutation.isPending || !internal_name || !slug || (!editing && !firstOrigin_id?.trim())}
            >
              {saveMutation.isPending ? 'Guardando…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate image options modal (product): destination, type, reference, include brand; optional "Ver instrucción". */}
      <Dialog
        open={generateImageModalOpen}
        onOpenChange={(open) => {
          setGenerateImageModalOpen(open);
          if (!open) setGenerateImageModalItem(null);
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Opciones de generación de imagen</DialogTitle>
          </DialogHeader>
          {generateImageModalItem && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Producto: <strong>{generateImageModalItem.internal_name}</strong>
              </p>
              <div className="space-y-2">
                <Label>Destino</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="genTarget"
                      checked={genTarget === 'packshot'}
                      onChange={() => setGenTarget('packshot')}
                      className="rounded-full"
                    />
                    Imagen principal (portada)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="genTarget"
                      checked={genTarget === 'secondary'}
                      onChange={() => setGenTarget('secondary')}
                      className="rounded-full"
                    />
                    Imágenes secundarias (añadir una)
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tipo de imagen</Label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="genImageType"
                      checked={genImageType === 'retail'}
                      onChange={() => setGenImageType('retail')}
                      className="rounded-full"
                    />
                    Retail (consumidor usando el producto)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="genImageType"
                      checked={genImageType === 'clean'}
                      onChange={() => setGenImageType('clean')}
                      className="rounded-full"
                    />
                    Producto limpio (centrado, nombre y formato)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="genImageType"
                      checked={genImageType === 'b2b'}
                      onChange={() => setGenImageType('b2b')}
                      className="rounded-full"
                    />
                    B2B (cajas, pallets, nombre, formato y uds/caja)
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Referencia (opcional)</Label>
                <Input
                  value={genReferenceUrl}
                  onChange={(e) => setGenReferenceUrl(e.target.value)}
                  placeholder="URL de imagen de referencia"
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="genIncludeBrand"
                  checked={genIncludeBrand}
                  onCheckedChange={(c) => setGenIncludeBrand(c === true)}
                />
                <Label htmlFor="genIncludeBrand" className="cursor-pointer font-normal">
                  Incluir marca (logo y nombre en la imagen)
                </Label>
              </div>
              {genPromptLoaded && (
                <div className="space-y-2">
                  <Label>Instrucción que se enviará (editable)</Label>
                  <Textarea
                    value={genPrompt}
                    onChange={(e) => setGenPrompt(e.target.value)}
                    rows={5}
                    className="font-mono text-sm"
                  />
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button type="button" variant="outline" size="sm" onClick={loadGenerateInstruction} disabled={genLoadingPrompt}>
                  {genLoadingPrompt ? 'Cargando…' : 'Ver instrucción'}
                </Button>
                <Button type="button" onClick={confirmGenerateImage} disabled={genGenerating}>
                  {genGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
                      Generando…
                    </>
                  ) : (
                    'Generar'
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={confirmGenerateImageBackground} disabled={genGenerating}>
                  Generar en segundo plano
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Origins Dialog */}
      <OriginsDialog
        open={originsOpen}
        onOpenChange={setOriginsOpen}
        item={selectedItem}
      />

      {/* Convert product to origin of another — use mutateAsync + try/catch so errors are always reported with context */}
      <ConvertToOriginDialog
        open={convertDialogOpen}
        onOpenChange={(open) => { setConvertDialogOpen(open); if (!open) setConvertSourceItem(null); }}
        sourceItem={convertSourceItem}
        targetCandidates={filteredItems.filter((i) => i.id !== convertSourceItem?.id)}
        onConfirm={async (targetItemId) => {
          if (!convertSourceItem) return;
          try {
            await convertToOriginOfMutation.mutateAsync({ sourceItemId: convertSourceItem.id, targetItemId });
          } catch (e) {
            reportCaughtError(e, {
              screen: 'admin_sourcing_items',
              action: 'convert_to_origin_of',
              source_item_id: convertSourceItem.id,
              target_item_id: targetItemId,
            });
            toast({ title: 'Error', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
          }
        }}
        isLoading={convertToOriginOfMutation.isPending}
      />

      {/* Items Table */}
      {isLoading ? <p className="text-secondary mt-6">Loading...</p> : (
        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 whitespace-nowrap">
                <span className="block font-medium text-xs">Sel.</span>
                <Checkbox
                  className="mt-1"
                  checked={paginatedItems.length > 0 && paginatedItems.every((i) => selectedIds.has(i.id))}
                  onCheckedChange={toggleAll}
                  aria-label="Seleccionar todas"
                />
              </TableHead>
              <TableHead className="w-[72px]">Thumbnail</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-16 text-center whitespace-nowrap" title="Icono de alerta si faltan datos de logística en el origen activo">
                Logística
              </TableHead>
              <TableHead>Format</TableHead>
              <TableHead className="w-[120px]">Generar imagen</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="w-12">
                  <Checkbox
                    checked={selectedIds.has(item.id)}
                    onCheckedChange={() => toggleRow(item.id)}
                    aria-label={`Seleccionar ${item.internal_name}`}
                  />
                </TableCell>
                <TableCell className="w-[72px]">
                  {item.packshot_image ? (
                    <div className="h-12 w-14 rounded border border-zinc-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                      <StoreThumbnailImg
                        coverUrl={item.packshot_image}
                        size={{ width: 56, height: 48 }}
                        className="max-h-full max-w-full w-auto h-auto object-contain"
                      />
                    </div>
                  ) : (
                    <span className="text-zinc-400 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Link to={`/store/product/${item.slug}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {item.internal_name}
                    </Link>
                    {hasPseo(item) ? (
                      (item.seo_metadata?.pseo_version ?? 0) >= currentPseoVersion ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-300 bg-green-50 text-green-700 whitespace-nowrap">v{item.seo_metadata?.pseo_version ?? '?'}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 bg-amber-50 text-amber-700 whitespace-nowrap">v{item.seo_metadata?.pseo_version ?? '?'}</Badge>
                      )
                    ) : (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-300 bg-red-50 text-red-700 whitespace-nowrap">Sin pSEO</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {(() => {
                    const activeOrigin = originsByItemId.get(item.id)?.find((o) => o.is_active) ?? null;
                    const missing = getMissingLogisticsLabels(activeOrigin, loadingMethods ?? []);
                    if (missing.length === 0) return <span className="text-zinc-300">—</span>;
                    return (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center justify-center text-amber-600" role="img" aria-label="Faltan datos de logística">
                            <AlertTriangle className="h-5 w-5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[240px]">
                          <p className="font-medium text-amber-800 mb-1">Faltan datos de logística</p>
                          <ul className="list-disc list-inside text-xs space-y-0.5">
                            {missing.map((label) => (
                              <li key={label}>{label}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{item.sales_format || '—'}</TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openGenerateImageModal(item)}
                    disabled={generatingOneIds.has(item.id)}
                    className="gap-1 shrink-0 min-w-[110px]"
                    title={item.packshot_image ? 'Regenerar imagen con IA' : 'Generar imagen con IA'}
                  >
                    {generatingOneIds.has(item.id) ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    {generatingOneIds.has(item.id) ? 'Generando imagen…' : 'Generar imagen'}
                  </Button>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => openOrigins(item)} className="gap-1">
                      <Globe className="h-3.5 w-3.5" />
                      Origins
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setConvertSourceItem(item); setConvertDialogOpen(true); }}
                      className="gap-1"
                      title="Convertir este producto en un origen secundario de otro producto"
                    >
                      <Merge className="h-3.5 w-3.5" />
                      Convertir a origen de
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(item.id)}>Delete</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {filteredItems.length > 0 && (
        <div className="flex items-center justify-between gap-4 mt-3">
          <p className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Dialog: pick target product so that source product becomes an (inactive) origin of the target. Source product is then deleted. */
function ConvertToOriginDialog({
  open,
  onOpenChange,
  sourceItem,
  targetCandidates,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceItem: SourcingItem | null;
  targetCandidates: SourcingItem[];
  onConfirm: (targetItemId: string) => void;
  isLoading: boolean;
}) {
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  useEffect(() => {
    if (open) setSelectedTargetId(targetCandidates[0]?.id ?? '');
  }, [open, targetCandidates]);

  if (!sourceItem) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Convertir producto en origen de otro</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          <strong>{sourceItem.internal_name}</strong> se eliminará y sus orígenes pasarán a ser orígenes secundarios (no activos) del producto que elijas. Quedará un solo producto con varios orígenes.
        </p>
        {targetCandidates.length === 0 ? (
          <p className="text-sm text-amber-600">No hay otros productos para elegir como destino (necesitas al menos otro ítem en la lista).</p>
        ) : (
          <div className="space-y-2">
            <Label>Producto destino (el que se queda con los orígenes)</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={selectedTargetId}
              onChange={(e) => setSelectedTargetId(e.target.value)}
            >
              {targetCandidates.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.internal_name} ({item.slug})
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={() => selectedTargetId && onConfirm(selectedTargetId)}
            disabled={!selectedTargetId || isLoading || targetCandidates.length === 0}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Merge className="h-4 w-4" />}
            {isLoading ? 'Convirtiendo…' : 'Convertir'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Dialog for managing origins of a specific item
function OriginsDialog({ open, onOpenChange, item }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: SourcingItem | null;
}) {
  const { data: origins, isLoading } = useSourcingItemOrigins(item?.id ?? null, { enabled: open && !!item?.id });
  const { data: allOrigins } = useOrigins();
  const { data: allOriginPorts } = useOriginPorts(); // All ports for display
  const { data: loadingMethods } = useLoadingMethods();
  const { data: suppliers } = useSuppliers();
  const { data: moqUnits } = useMoqUnits();
  const { data: costIncoterms } = useCostIncoterms();
  const saveOrigin = useSaveItemOrigin();
  const deleteOrigin = useDeleteItemOrigin();
  const setActiveOrigin = useSetActiveOrigin();
  const { toast } = useToast();

  // Edit origin dialog state
  const [editingOrigin, setEditingOrigin] = useState<SourcingItemOrigin | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  // Expanded row in origins table (show full details)
  const [expandedOriginId, setExpandedOriginId] = useState<string | null>(null);

  // Origin form state
  const [origin_id, setOrigin_id] = useState('');
  const [origin_port_id, setOrigin_port_id] = useState('');
  const [supplier_id, setSupplier_id] = useState('');
  const { data: originPorts } = useOriginPorts(origin_id || undefined);
  const [cost_usd, setCost_usd] = useState('');
  const [cost_incoterm_id, setCost_incoterm_id] = useState('');
  const [units_per_box, setUnits_per_box] = useState('');
  const [boxes_per_pallet, setBoxes_per_pallet] = useState('');
  const [boxes_per_container, setBoxes_per_container] = useState('');
  const [gross_weight_per_box_kg, setGross_weight_per_box_kg] = useState('');
  const [moq_value, setMoq_value] = useState('');
  const [moq_unit_id, setMoq_unit_id] = useState('');
  const [production_time_days, setProduction_time_days] = useState('');
  const [payment_terms, setPayment_terms] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [internal_notes, setInternal_notes] = useState('');
  const [referencia, setReferencia] = useState('');
  const [loading_method_id, setLoading_method_id] = useState('');
  const [recommended_container_type, setRecommended_container_type] = useState('');
  const [box_length_cm, setBox_length_cm] = useState('');
  const [box_width_cm, setBox_width_cm] = useState('');
  const [box_height_cm, setBox_height_cm] = useState('');

  function resetOriginForm() {
    setOrigin_id(allOrigins?.[0]?.id ?? '');
    setOrigin_port_id('');
    setSupplier_id('');
    setCost_usd('');
    setCost_incoterm_id(costIncoterms?.[0]?.id ?? '');
    setUnits_per_box('');
    setBoxes_per_pallet('');
    setBoxes_per_container('');
    setGross_weight_per_box_kg('');
    setMoq_value('');
    setMoq_unit_id(moqUnits?.[0]?.id ?? '');
    setProduction_time_days('');
    setPayment_terms('');
    setObservaciones('');
    setInternal_notes('');
    setReferencia('');
    setLoading_method_id('');
    setRecommended_container_type('');
    setBox_length_cm('');
    setBox_width_cm('');
    setBox_height_cm('');
    setEditingOrigin(null);
  }

  function openEditOrigin(origin: SourcingItemOrigin) {
    setEditingOrigin(origin);
    setOrigin_id(origin.origin_id);
    setOrigin_port_id(origin.origin_port_id ?? '');
    setSupplier_id(origin.supplier_id ?? '');
    setCost_usd(String(origin.cost_usd ?? ''));
    setCost_incoterm_id(origin.cost_incoterm_id ?? '');
    setUnits_per_box(String(origin.units_per_box ?? ''));
    setBoxes_per_pallet(String(origin.boxes_per_pallet ?? ''));
    setBoxes_per_container(String(origin.boxes_per_container ?? ''));
    setGross_weight_per_box_kg(String(origin.gross_weight_per_box_kg ?? ''));
    setMoq_value(String(origin.moq_value ?? ''));
    setMoq_unit_id(origin.moq_unit_id ?? '');
    setProduction_time_days(String(origin.production_time_days ?? ''));
    setPayment_terms(origin.payment_terms ?? '');
    setObservaciones(origin.observaciones ?? '');
    setInternal_notes(origin.internal_notes ?? '');
    setReferencia(origin.referencia ?? '');
    setLoading_method_id(origin.loading_method_id ?? '');
    setRecommended_container_type(origin.recommended_container_type ?? '');
    setBox_length_cm(origin.box_length_cm != null ? String(origin.box_length_cm) : '');
    setBox_width_cm(origin.box_width_cm != null ? String(origin.box_width_cm) : '');
    setBox_height_cm(origin.box_height_cm != null ? String(origin.box_height_cm) : '');
    setEditOpen(true);
  }

  async function handleSaveOrigin() {
    if (!item) return;
    try {
      await saveOrigin.mutateAsync({
        id: editingOrigin?.id,
        sourcing_item_id: item.id,
        origin_id,
        origin_port_id: origin_port_id || null,
        supplier_id: supplier_id || null,
        cost_usd: cost_usd ? Number(cost_usd) : null,
        cost_incoterm_id: cost_incoterm_id || null,
        loading_method_id: loading_method_id || null,
        recommended_container_type: recommended_container_type || null,
        units_per_box: Number(units_per_box) || 0,
        boxes_per_pallet: boxes_per_pallet ? Number(boxes_per_pallet) : null,
        boxes_per_container: Number(boxes_per_container) || 0,
        gross_weight_per_box_kg: Number(gross_weight_per_box_kg) || 0,
        moq_value: Number(moq_value) || 0,
        moq_unit_id: moq_unit_id || null,
        production_time_days: production_time_days ? Number(production_time_days) : null,
        payment_terms: payment_terms || null,
        observaciones: observaciones || null,
        internal_notes: internal_notes?.trim() || null,
        referencia: referencia?.trim() || null,
        is_active: editingOrigin?.is_active ?? (origins?.length === 0), // First origin is active by default
        box_length_cm: box_length_cm === '' ? null : Number(box_length_cm),
        box_width_cm: box_width_cm === '' ? null : Number(box_width_cm),
        box_height_cm: box_height_cm === '' ? null : Number(box_height_cm),
      });
      setEditOpen(false);
      resetOriginForm();
      toast({ title: editingOrigin ? 'Origin updated' : 'Origin added' });
    } catch (e) {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'save_origin', item_id: item?.id, editing_origin_id: editingOrigin?.id });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' });
    }
  }

  async function handleDelete(originId: string) {
    if (!item) return;
    try {
      await deleteOrigin.mutateAsync({ id: originId, itemId: item.id });
      toast({ title: 'Origin deleted' });
    } catch (e) {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'delete_origin', item_id: item.id, origin_id: originId });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' });
    }
  }

  async function handleSetActive(originId: string) {
    if (!item) return;
    try {
      await setActiveOrigin.mutateAsync({ originId, itemId: item.id });
      toast({ title: 'Origin set as active' });
    } catch (e) {
      reportCaughtError(e, { screen: 'admin_sourcing_items', action: 'set_active_origin', item_id: item.id, origin_id: originId });
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' });
    }
  }

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Origins for: {item.internal_name}
            {!isLoading && origins != null && (
              <span className="font-normal text-muted-foreground ml-2">
                ({origins.length} {origins.length === 1 ? 'origen' : 'orígenes'})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Same product can be sourced from different origins. Mark one as active for public display.
            </p>
            <Button size="sm" onClick={() => { resetOriginForm(); setEditOpen(true); }} className="gap-1">
              <Plus className="h-4 w-4" />
              Add Origin
            </Button>
          </div>

          {isLoading ? (
            <p className="text-muted-foreground">Loading origins...</p>
          ) : origins?.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No origins yet. Add one to get started.</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" aria-label="Expand" />
                    <TableHead className="w-[100px]">Activo</TableHead>
                    <TableHead>Localización</TableHead>
                    <TableHead className="whitespace-nowrap">Coste</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="w-[140px] text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {origins?.map((origin) => {
                    const originName = allOrigins?.find((o) => o.id === origin.origin_id)?.name ?? 'Unknown';
                    const portName = allOriginPorts?.find((p) => p.id === origin.origin_port_id)?.name;
                    const supplierName = suppliers?.find((s) => s.id === origin.supplier_id)?.name;
                    const moqUnitName = moqUnits?.find((m) => m.id === origin.moq_unit_id)?.name ?? '';
                    const loadingMethodName = loadingMethods?.find((l) => l.id === origin.loading_method_id)?.name;
                    const incotermName = costIncoterms?.find((c) => c.id === origin.cost_incoterm_id)?.name;
                    const isExpanded = expandedOriginId === origin.id;

                    return (
                      <Fragment key={origin.id}>
                        <TableRow
                          className={origin.is_active ? 'bg-emerald-50/70' : undefined}
                        >
                          <TableCell className="p-1 w-8">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setExpandedOriginId((id) => (id === origin.id ? null : origin.id))}
                              aria-expanded={isExpanded}
                              title={isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                            >
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          </TableCell>
                          <TableCell>
                            {origin.is_active ? (
                              <Badge variant="default" className="gap-1 bg-emerald-600">
                                <Check className="h-3 w-3" />
                                Activo
                              </Badge>
                            ) : (
                              <Button variant="outline" size="sm" onClick={() => handleSetActive(origin.id)}>
                                Activar
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-4 w-4 text-zinc-500 shrink-0" />
                              <span className="font-medium">{originName}</span>
                              {portName && <span className="text-muted-foreground text-sm">({portName})</span>}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {origin.cost_usd != null ? (
                              <span className="font-medium">${Number(origin.cost_usd).toFixed(2)} {incotermName ?? ''}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {supplierName ?? <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditOrigin(origin)}>Editar</Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(origin.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${origin.id}-detail`}>
                            <TableCell colSpan={6} className="bg-zinc-50/80 p-4">
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5 text-zinc-500">
                                    <Package className="h-4 w-4" />
                                    <span className="font-medium">Pedido Mínimo</span>
                                  </div>
                                  <p className="text-zinc-900 font-semibold">{origin.moq_value} {moqUnitName}</p>
                                </div>
                                {origin.production_time_days != null && (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-zinc-500">
                                      <Clock className="h-4 w-4" />
                                      <span className="font-medium">Producción</span>
                                    </div>
                                    <p className="text-zinc-900 font-semibold">{origin.production_time_days} días</p>
                                  </div>
                                )}
                                {origin.payment_terms && (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-zinc-500">
                                      <CreditCard className="h-4 w-4" />
                                      <span className="font-medium">Forma de Pago</span>
                                    </div>
                                    <p className="text-zinc-900">{origin.payment_terms}</p>
                                  </div>
                                )}
                                {loadingMethodName && (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-zinc-500">
                                      <Truck className="h-4 w-4" />
                                      <span className="font-medium">Carga</span>
                                    </div>
                                    <p className="text-zinc-900">{loadingMethodName}</p>
                                  </div>
                                )}
                                {(origin.units_per_box || origin.boxes_per_container) ? (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-zinc-500">
                                      <Package className="h-4 w-4" />
                                      <span className="font-medium">Logística</span>
                                    </div>
                                    <div className="text-zinc-900 text-xs space-y-0.5">
                                      {origin.units_per_box > 0 && <p>{origin.units_per_box} uds/caja</p>}
                                      {origin.boxes_per_pallet && <p>{origin.boxes_per_pallet} cajas/pallet</p>}
                                      {origin.boxes_per_container > 0 && <p>{origin.boxes_per_container.toLocaleString()} cajas/contenedor</p>}
                                    </div>
                                  </div>
                                ) : null}
                                {origin.gross_weight_per_box_kg > 0 && (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-zinc-500">
                                      <Scale className="h-4 w-4" />
                                      <span className="font-medium">Peso Bruto</span>
                                    </div>
                                    <p className="text-zinc-900">{origin.gross_weight_per_box_kg} kg/caja</p>
                                  </div>
                                )}
                              </div>
                              {origin.cost_usd != null && (
                                <div className="mt-3 py-2 px-3 bg-amber-50 border border-amber-200 rounded flex items-center gap-2">
                                  <span className="text-amber-700 text-sm font-medium">Costo (oculto):</span>
                                  <span className="text-amber-900 font-semibold">${origin.cost_usd} {incotermName ?? ''}</span>
                                </div>
                              )}
                              {origin.referencia && (
                                <div className="mt-2 py-2 px-3 bg-zinc-100 rounded flex items-center gap-2">
                                  <span className="text-zinc-500 text-sm font-medium">Referencia:</span>
                                  <span className="text-zinc-800 font-mono text-sm">{origin.referencia}</span>
                                </div>
                              )}
                              {origin.internal_notes && (
                                <div className="mt-2 py-2 px-3 bg-amber-50 border border-amber-200 rounded">
                                  <p className="text-xs text-amber-700 font-medium mb-1">Info. interna</p>
                                  <p className="text-sm text-zinc-700">{origin.internal_notes}</p>
                                </div>
                              )}
                              {origin.observaciones && (
                                <div className="mt-2 py-2 px-3 bg-zinc-100 rounded">
                                  <p className="text-xs text-zinc-500 font-medium mb-1">Observaciones</p>
                                  <p className="text-sm text-zinc-700">{origin.observaciones}</p>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Edit Origin Sub-Dialog */}
        <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) resetOriginForm(); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingOrigin ? 'Edit Origin' : 'Add Origin'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Origin (Country)</Label>
                <select 
                  className="w-full border rounded px-3 py-2" 
                  value={origin_id} 
                  onChange={(e) => { setOrigin_id(e.target.value); setOrigin_port_id(''); }}
                  disabled={!!editingOrigin} // Can't change origin when editing
                >
                  {(allOrigins ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Origin Port</Label>
                <select className="w-full border rounded px-3 py-2" value={origin_port_id} onChange={(e) => setOrigin_port_id(e.target.value)}>
                  <option value="">— Select port —</option>
                  {(originPorts ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Supplier (proveedor para esta carga)</Label>
                <select className="w-full border rounded px-3 py-2" value={supplier_id} onChange={(e) => setSupplier_id(e.target.value)}>
                  <option value="">— Sin supplier —</option>
                  {(suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Cost USD por caja (interno)</Label><Input type="number" value={cost_usd} onChange={(e) => setCost_usd(e.target.value)} /></div>
                <div>
                  <Label>Cost Incoterm</Label>
                  <select className="w-full border rounded px-3 py-2" value={cost_incoterm_id} onChange={(e) => setCost_incoterm_id(e.target.value)}>
                    {(costIncoterms ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Units per box</Label><Input type="number" value={units_per_box} onChange={(e) => setUnits_per_box(e.target.value)} /></div>
                <div><Label>Boxes per pallet</Label><Input type="number" value={boxes_per_pallet} onChange={(e) => setBoxes_per_pallet(e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Cajas por contenedor</Label><Input type="number" value={boxes_per_container} onChange={(e) => setBoxes_per_container(e.target.value)} /></div>
                <div><Label>Gross weight per box (kg)</Label><Input type="number" value={gross_weight_per_box_kg} onChange={(e) => setGross_weight_per_box_kg(e.target.value)} /></div>
              </div>
              <div>
                <Label>Box dimensions (cm) — L × W × H</Label>
                <div className="flex gap-2 items-center mt-1">
                  <Input type="number" min={0} placeholder="L" value={box_length_cm} onChange={(e) => setBox_length_cm(e.target.value)} className="w-20" />
                  <span className="text-zinc-400">×</span>
                  <Input type="number" min={0} placeholder="W" value={box_width_cm} onChange={(e) => setBox_width_cm(e.target.value)} className="w-20" />
                  <span className="text-zinc-400">×</span>
                  <Input type="number" min={0} placeholder="H" value={box_height_cm} onChange={(e) => setBox_height_cm(e.target.value)} className="w-20" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Método de carga</Label>
                  <select className="w-full border rounded px-3 py-2" value={loading_method_id} onChange={(e) => setLoading_method_id(e.target.value)}>
                    <option value="">— Opcional —</option>
                    {(loadingMethods ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Contenedor recomendado</Label>
                  <select className="w-full border rounded px-3 py-2" value={recommended_container_type} onChange={(e) => setRecommended_container_type(e.target.value)}>
                    <option value="">— Opcional —</option>
                    <option value="20DV">20ft Dry</option>
                    <option value="40HQ">40ft Dry</option>
                    <option value="20REF">20ft Refer</option>
                    <option value="40REF">40ft Refer</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>MOQ value</Label><Input type="number" value={moq_value} onChange={(e) => setMoq_value(e.target.value)} /></div>
                <div>
                  <Label>MOQ unit</Label>
                  <select className="w-full border rounded px-3 py-2" value={moq_unit_id} onChange={(e) => setMoq_unit_id(e.target.value)}>
                    {(moqUnits ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Production time (days)</Label><Input type="number" value={production_time_days} onChange={(e) => setProduction_time_days(e.target.value)} placeholder="e.g. 45" /></div>
                <div><Label>Payment terms</Label><Input value={payment_terms} onChange={(e) => setPayment_terms(e.target.value)} placeholder="e.g. 30% deposit, 70% before loading" /></div>
              </div>
              <div>
                <Label>Referencia (código proveedor)</Label>
                <Input value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Código del proveedor para cotización (interno)" />
              </div>
              <div>
                <Label>Observaciones</Label>
                <textarea 
                  className="w-full border rounded px-3 py-2 min-h-[80px]" 
                  value={observaciones} 
                  onChange={(e) => setObservaciones(e.target.value)} 
                  placeholder="Notes specific to this origin..."
                />
              </div>
              <div>
                <Label>Info. interna (solo admin)</Label>
                <textarea 
                  className="w-full border rounded px-3 py-2 min-h-[60px] bg-amber-50/50" 
                  value={internal_notes} 
                  onChange={(e) => setInternal_notes(e.target.value)} 
                  placeholder="Notas para compra/coste; no al cliente"
                />
              </div>
              <Button onClick={handleSaveOrigin} disabled={saveOrigin.isPending || !origin_id}>
                {saveOrigin.isPending ? 'Saving...' : 'Save Origin'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
